import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { SettingsScreen, GameSettings } from '../settings/SettingsScreen';
import { User, Trophy, Target, TrendingUp, Zap, Calendar, Settings, LogOut, Crown, Gem } from 'lucide-react';
import { useAppContext } from '../../contexts/AppContext';
interface ProfileScreenProps {
  settings: GameSettings;
  onSettingsChange: (settings: GameSettings) => void;
}
export function ProfileScreen({
  settings,
  onSettingsChange
}: ProfileScreenProps) {
  const {
    user,
    signOut
  } = useAppContext();
  const [showSettings, setShowSettings] = useState(false);
  if (!user) {
    return <div className="flex flex-col h-full bg-slate-50 items-center justify-center p-6" data-id="element-438">
        <div className="text-center" data-id="element-439">
          <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-440">
            <User className="text-slate-400" size={40} data-id="element-441" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-442">
            Sign In Required
          </h2>
          <p className="text-slate-600 mb-6" data-id="element-443">
            Create an account to track your progress and stats
          </p>
          <Button size="lg" data-id="element-444">Sign In / Sign Up</Button>
        </div>
      </div>;
  }
  const xpProgress = user.xp / 1000 * 100;
  const winRate = user.totalGames > 0 ? Math.round(user.wins / user.totalGames * 100) : 0;
  // Format play time
  const hours = Math.floor(user.totalPlayTime / 3600);
  const minutes = Math.floor(user.totalPlayTime % 3600 / 60);
  const playTimeFormatted = `${hours}h ${minutes}m`;
  // Format join date
  const joinDate = new Date(user.joinDate).toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric'
  });
  // Check achievements
  const achievements = [{
    id: 'first_win',
    name: 'First Win',
    icon: '🏆',
    unlocked: user.wins >= 1
  }, {
    id: 'win_streak_10',
    name: '10 Win Streak',
    icon: '🔥',
    unlocked: user.bestStreak >= 10
  }, {
    id: 'speed_demon',
    name: 'Speed Demon',
    icon: '⚡',
    unlocked: user.totalGames >= 50
  }, {
    id: 'century_club',
    name: 'Century Club',
    icon: '💯',
    unlocked: user.wins >= 100
  }, {
    id: 'master',
    name: 'Master',
    icon: '👑',
    unlocked: user.level >= 50
  }, {
    id: 'legend',
    name: 'Legend',
    icon: '⭐',
    unlocked: user.level >= 100
  }];
  const handleSignOut = () => {
    if (confirm('Are you sure you want to sign out?')) {
      signOut();
    }
  };
  return <div className="flex flex-col h-full bg-slate-50 overflow-y-auto" data-id="element-445">
      {/* Header */}
      <div className="bg-gradient-to-br from-blue-500 to-violet-600 text-white p-6 pb-12" data-id="element-446">
        <div className="flex items-center justify-between mb-6" data-id="element-447">
          <h1 className="text-2xl font-black" data-id="element-448">Profile</h1>
          <button onClick={() => setShowSettings(true)} className="p-2 hover:bg-white/20 rounded-lg transition-colors" data-id="element-449">
            <Settings size={24} data-id="element-450" />
          </button>
        </div>

        {/* Avatar & Name */}
        <div className="flex flex-col items-center" data-id="element-451">
          <div className="w-24 h-24 rounded-full bg-white flex items-center justify-center text-5xl mb-3 shadow-xl" data-id="element-452">
            👤
          </div>
          <h2 className="text-2xl font-bold mb-1" data-id="element-453">{user.name}</h2>
          <div className="flex items-center gap-2" data-id="element-454">
            <Badge className="bg-white/20 text-white border-white/30" data-id="element-455">
              Level {user.level}
            </Badge>
            <Badge className="bg-amber-500 text-white" data-id="element-456">
              <Crown size={12} className="mr-1" data-id="element-457" />
              Rank #12
            </Badge>
          </div>
        </div>
      </div>

      <div className="px-4 -mt-8 pb-24 space-y-4" data-id="element-458">
        {/* XP Progress */}
        <Card className="bg-white shadow-lg" data-id="element-459">
          <div className="flex items-center justify-between mb-3" data-id="element-460">
            <span className="text-sm font-semibold text-slate-600" data-id="element-461">
              Level Progress
            </span>
            <span className="text-sm font-bold text-slate-900" data-id="element-462">
              {user.xp} / 1000 XP
            </span>
          </div>
          <div className="h-3 bg-slate-100 rounded-full overflow-hidden" data-id="element-463">
            <div className="h-full bg-gradient-to-r from-blue-500 to-violet-600 rounded-full transition-all duration-500" style={{
            width: `${xpProgress}%`
          }} data-id="element-464" />
          </div>
        </Card>

        {/* Currency */}
        <Card data-id="element-465">
          <div className="flex items-center justify-between" data-id="element-466">
            <div className="flex items-center gap-3" data-id="element-467">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center" data-id="element-468">
                <Gem className="text-white" size={24} data-id="element-469" />
              </div>
              <div data-id="element-470">
                <h3 className="font-bold text-slate-900" data-id="element-471">Diamonds</h3>
                <p className="text-sm text-slate-500" data-id="element-472">Premium currency</p>
              </div>
            </div>
            <div className="text-right" data-id="element-473">
              <div className="text-2xl font-black text-slate-900" data-id="element-474">
                {user.diamonds}
              </div>
              <Button size="sm" variant="outline" className="mt-1" data-id="element-475">
                Buy More
              </Button>
            </div>
          </div>
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-3" data-id="element-476">
          <Card className="text-center" data-id="element-477">
            <Trophy className="text-amber-500 mx-auto mb-2" size={24} data-id="element-478" />
            <div className="text-2xl font-black text-slate-900" data-id="element-479">
              {user.wins}
            </div>
            <div className="text-xs text-slate-500" data-id="element-480">Total Wins</div>
          </Card>
          <Card className="text-center" data-id="element-481">
            <Target className="text-blue-500 mx-auto mb-2" size={24} data-id="element-482" />
            <div className="text-2xl font-black text-slate-900" data-id="element-483">
              {user.totalGames}
            </div>
            <div className="text-xs text-slate-500" data-id="element-484">Games Played</div>
          </Card>
          <Card className="text-center" data-id="element-485">
            <TrendingUp className="text-emerald-500 mx-auto mb-2" size={24} data-id="element-486" />
            <div className="text-2xl font-black text-slate-900" data-id="element-487">{winRate}%</div>
            <div className="text-xs text-slate-500" data-id="element-488">Win Rate</div>
          </Card>
          <Card className="text-center" data-id="element-489">
            <Zap className="text-orange-500 mx-auto mb-2" size={24} data-id="element-490" />
            <div className="text-2xl font-black text-slate-900" data-id="element-491">
              {user.currentStreak}
            </div>
            <div className="text-xs text-slate-500" data-id="element-492">Current Streak</div>
          </Card>
        </div>

        {/* Achievements */}
        <Card data-id="element-493">
          <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-494">
            <Trophy size={20} className="text-amber-500" data-id="element-495" />
            Achievements
          </h3>
          <div className="grid grid-cols-3 gap-3" data-id="element-496">
            {achievements.map(achievement => <div key={achievement.id} className={`flex flex-col items-center p-3 rounded-xl ${achievement.unlocked ? 'bg-amber-50' : 'bg-slate-100 opacity-50'}`} data-id="element-497">
                <div className="text-3xl mb-1" data-id="element-498">{achievement.icon}</div>
                <div className="text-xs font-semibold text-center text-slate-700" data-id="element-499">
                  {achievement.name}
                </div>
              </div>)}
          </div>
        </Card>

        {/* Account Info */}
        <Card data-id="element-500">
          <h3 className="font-bold text-slate-900 mb-3" data-id="element-501">Account Info</h3>
          <div className="space-y-2 text-sm" data-id="element-502">
            <div className="flex justify-between" data-id="element-503">
              <span className="text-slate-500" data-id="element-504">Member Since</span>
              <span className="font-semibold text-slate-900" data-id="element-505">{joinDate}</span>
            </div>
            <div className="flex justify-between" data-id="element-506">
              <span className="text-slate-500" data-id="element-507">Total Play Time</span>
              <span className="font-semibold text-slate-900" data-id="element-508">
                {playTimeFormatted}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-509">
              <span className="text-slate-500" data-id="element-510">Best Streak</span>
              <span className="font-semibold text-slate-900" data-id="element-511">
                {user.bestStreak} wins
              </span>
            </div>
          </div>
        </Card>

        {/* Actions */}
        <div className="space-y-2" data-id="element-512">
          <Button fullWidth variant="outline" className="gap-2" onClick={() => setShowSettings(true)} data-id="element-513">
            <Settings size={20} data-id="element-514" />
            Settings
          </Button>
          <Button fullWidth variant="outline" className="gap-2 text-red-600 border-red-200 hover:bg-red-50" onClick={handleSignOut} data-id="element-515">
            <LogOut size={20} data-id="element-516" />
            Sign Out
          </Button>
        </div>
      </div>

      {/* Settings Modal */}
      <SettingsScreen isOpen={showSettings} onClose={() => setShowSettings(false)} settings={settings} onSave={newSettings => {
      onSettingsChange(newSettings);
      setShowSettings(false);
    }} data-id="element-517" />
    </div>;
}